/**
 * Table Component - The Intelligent Standard
 *
 * A refined data table with sophisticated styling for
 * clear data presentation.
 */

import { HTMLAttributes, ReactNode } from 'react';

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  children: ReactNode;
}

export function Table({ children, className = '', ...props }: TableProps) {
  return (
    <div className="overflow-x-auto rounded-xl border border-cream-dark">
      <table
        className={`w-full border-collapse bg-white ${className}`}
        {...props}
      >
        {children}
      </table>
    </div>
  );
}

export interface TableHeadProps {
  children: ReactNode;
}

export function TableHead({ children }: TableHeadProps) {
  return <thead className="bg-cream">{children}</thead>;
}

export interface TableBodyProps {
  children: ReactNode;
}

export function TableBody({ children }: TableBodyProps) {
  return <tbody className="divide-y divide-cream-dark">{children}</tbody>;
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
  hover?: boolean;
}

export function TableRow({
  children,
  hover = true,
  className = '',
  ...props
}: TableRowProps) {
  return (
    <tr
      className={`
        ${hover ? 'hover:bg-cream/50' : ''}
        transition-colors duration-150
        ${className}
      `}
      {...props}
    >
      {children}
    </tr>
  );
}

export interface TableHeaderCellProps extends HTMLAttributes<HTMLTableCellElement> {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

export function TableHeaderCell({
  children,
  align = 'left',
  width,
  className = '',
  ...props
}: TableHeaderCellProps) {
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];

  return (
    <th
      className={`
        px-6 py-4
        text-caption text-slate font-medium
        uppercase tracking-wider
        border-b border-cream-dark
        ${alignClass}
        ${className}
      `}
      style={{ width }}
      {...props}
    >
      {children}
    </th>
  );
}

export interface TableCellProps extends HTMLAttributes<HTMLTableCellElement> {
  children: ReactNode;
  align?: 'left' | 'center' | 'right';
}

export function TableCell({
  children,
  align = 'left',
  className = '',
  ...props
}: TableCellProps) {
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];

  return (
    <td
      className={`
        px-6 py-4
        text-body text-ink
        ${alignClass}
        ${className}
      `}
      {...props}
    >
      {children}
    </td>
  );
}

/**
 * Empty state for tables with no data
 */
export function TableEmptyState({
  message = 'No data available',
  colSpan = 1,
}: {
  message?: string;
  colSpan?: number;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-center">
        <div className="text-slate">
          <svg
            className="w-12 h-12 mx-auto mb-3 opacity-30"
            viewBox="0 0 24 24"
            fill="none"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <line
              x1="3"
              y1="9"
              x2="21"
              y2="9"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <line
              x1="9"
              y1="9"
              x2="9"
              y2="21"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
          <p className="text-body-sm">{message}</p>
        </div>
      </td>
    </tr>
  );
}
